import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import {
  BookingModeDto,
  CustomerPropertiesDto,
  PermissionDto,
  ProcessCreationDefaultDataMatchDto,
} from '@/api/generated';
import { useAddNoteToResource } from '@/components/addNoteToResource/components/noteDialog/util/useAddNoteToResource.util.ts';
import { FormProviderRc } from '@/components/form/FormProviderRC.comp.tsx';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useHasPermission } from '@/contexts/userContext/util/useHasPermission.util.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { hasRouteAccess } from '@/models/route/routeAccess.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { customerStatusUtil } from '@/views/customers/status/customerStatus.util.ts';
import { CustomerStatusChip } from '@/views/customers/status/CustomerStatusChip.comp.tsx';
import { useEmailsHandlers } from '@/views/emails/util/useEmailsHandlers.util.ts';
import { useProcessCalendarEntryQueryBookingMode } from '@/views/processes/calendarEntryQuery/util/useProcessCalendarEntryQueryBookingMode.util.ts';
import { useProcessCalendarEntryQueryOptions } from '@/views/processes/calendarEntryQuery/util/useProcessCalendarEntryQueryOptions.util.ts';
import { processCalendarEntryQueryUtil } from '@/views/processes/calendarEntryQuery/utils/processCalendarEntryQuery.util.ts';
import { ProcessCreateForm } from '@/views/processesCreate/components/ProcessCreateForm.comp.tsx';
import { processCreateDialogStyles } from '@/views/processesCreate/ProcessCreateDialog.styles.ts';
import {
  CreateProcessFormModel,
  CreateProcessFormValidationContext,
} from '@/views/processesCreate/model/CreateProcessForm.model.ts';
import { ProcessCreateDialogRequest } from '@/views/processesCreate/model/ProcessCreateDialog.model.ts';
import { processCreateDialogAtom } from '@/views/processesCreate/model/ProcessCreateDialog.state.ts';
import { useCreateProcess } from '@/views/processesCreate/util/useCreateProcess.util.ts';
import { useCreateProcessFormValidation } from '@/views/processesCreate/util/useCreateProcessForm.validation.ts';
import { useGetMatchCustomerStatuses } from '@/views/processesCreate/util/useGetMatchCustomerStatuses.util.ts';
import { useGetProcessCreationDefaultData } from '@/views/processesCreate/util/useGetProcessCreationDefaultData.util.ts';
import { useProcessCreateOffer } from '@/views/processesCreate/util/useProcessCreateOffer.util.ts';
import { useGetContactDetails } from '@/views/contactsDetails/util/useGetContactDetails.util.ts';
import { processesCreateUtil } from '@/views/processesCreate/utils/processesCreate.util.ts';

export const ProcessCreateDialog: React.FC = () => {
  const request = useAtomValue(processCreateDialogAtom);
  const { permissions } = useUserContext();

  if (!request || !permissions.includes(PermissionDto.ProcessCreate)) {
    return null;
  }

  return <ProcessCreateDialogForm request={request} />;
};

const ProcessCreateDialogForm: React.FC<{ request: ProcessCreateDialogRequest }> = ({
  request,
}) => {
  const { t } = useTranslationWithPrefix('views.processesCreate');
  const { t: emailT } = useTranslationWithPrefix('views.emails.threadEmails.dialog.newProcess');
  const { t: tPicker } = useTranslationWithPrefix('views.customers.status.processPicker');
  const validation = useCreateProcessFormValidation();
  const { createProcess, isCreatingProcess } = useCreateProcess();
  const { USERS_SHORT } = useDictionariesContext();
  const { addResourceNote, isAddingResourceNote } = useAddNoteToResource({
    disableSuccessAlert: true,
  });
  const { permissions, userId, userSelectOption } = useUserContext();
  const canCreateOffer = useHasPermission(PermissionDto.OfferCreate);
  const setRequest = useSetAtom(processCreateDialogAtom);
  const { refetchCurrentThreads } = useEmailsHandlers();
  const { customersApi } = useApiClient();
  const {
    isError: isDefaultDataError,
    isGetting: isGettingDefaultData,
    processCreationDefaultData,
  } = useGetProcessCreationDefaultData({
    email: request.senderEmail,
    emailThreadId: request.emailThreadId,
  });
  const matches = processCreationDefaultData?.matches ?? [];
  const { isLoading: isLoadingMatchStatuses, statuses: matchStatuses } =
    useGetMatchCustomerStatuses(matches);
  const [selectedMatch, setSelectedMatch] = useState<ProcessCreationDefaultDataMatchDto | null>(
    null,
  );
  const activeMatch =
    selectedMatch ??
    processesCreateUtil.getDefaultMatch(matches, matchStatuses, isLoadingMatchStatuses);
  const matchOwnerOption = useMemo(() => {
    if (!activeMatch) return undefined;
    return processesCreateUtil.getMatchOwnerOption(USERS_SHORT, activeMatch);
  }, [activeMatch, USERS_SHORT]);
  const usersOptions =
    matchOwnerOption && !USERS_SHORT.some(user => user.value === matchOwnerOption.value)
      ? [...USERS_SHORT, matchOwnerOption]
      : USERS_SHORT;
  const contactId = activeMatch?.contactId ?? request.contactId;
  const { contactDetails } = useGetContactDetails({ contactId });
  const contact = contactDetails?.basicContact;
  const customerId = activeMatch?.customerId ?? contact?.customerId ?? request.customerId;
  const knownCustomerType = contact?.customerType ?? request.customerType;

  const { data: fetchedCustomerType } = useQuery({
    enabled: Boolean(customerId && !knownCustomerType),
    queryFn: () => processesCreateUtil.getCustomerType(customersApi, customerId),
    queryKey: [QueryKeyEnum.CUSTOMERS.TYPE, customerId],
  });
  const customerType = knownCustomerType ?? fetchedCustomerType;

  const { data: customer } = useQuery<CustomerPropertiesDto | undefined>({
    enabled: Boolean(customerId && customerType),
    queryFn: () => processesCreateUtil.getCustomerDetails(customersApi, customerId, customerType),
    queryKey: [QueryKeyEnum.CUSTOMERS.DETAILS, customerId],
  });

  const [selectedType, setSelectedType] = useState(request.requestedDictionaryType);
  const [selectedEntryId, setSelectedEntryId] = useState(request.requestedDictionaryId);
  const [isOfferRequired, setIsOfferRequired] = useState(false);
  const {
    bookingMode,
    calendarsEnabled,
    entryOptions,
    isEntryInactive,
    isError: isCalendarEntryQueryError,
    isLoading: isLoadingCalendarEntryQuery,
    typeLabel,
    typeOptions,
  } = useProcessCalendarEntryQueryOptions(selectedType, selectedEntryId);
  // the booking mode is applied to the prefilled range by the effect below, without resetting the form
  const initialCalendarEntryQuery = useMemo(
    () => processCalendarEntryQueryUtil.fromValues(request, typeLabel, BookingModeDto.FullDay),
    [request, typeLabel],
  );

  const formMethods = useForm<CreateProcessFormModel, CreateProcessFormValidationContext>({
    context: { bookingMode, isOfferRequired },
    defaultValues: {
      ...initialCalendarEntryQuery,
      contact: null,
      customer: null,
      emailThreadId: request.emailThreadId,
      name: '',
      noteText: '',
      offerBookingTarget: { definition: null, item: null },
      processOwnerId: userSelectOption,
    },
    resolver: yupResolver(validation),
  });
  const { dirtyFields } = formMethods.formState;
  // RHF recomputes dirtyFields of the whole form on a user change, so the auto name set by
  // setValue would count as dirty; only a real edit of the field makes the name manual
  const [nameEditedFor, setNameEditedFor] = useState<ProcessCreateDialogRequest | null>(null);
  const isNameEdited = nameEditedFor === request;
  const [watchedType, requestedEntry, requestedFrom, requestedTo] = formMethods.watch([
    'requestedDictionaryType',
    'requestedDictionaryId',
    'requestedFrom',
    'requestedTo',
  ]);
  if (watchedType?.value !== selectedType) setSelectedType(watchedType?.value);
  if (requestedEntry?.value !== selectedEntryId) setSelectedEntryId(requestedEntry?.value);
  const offer = useProcessCreateOffer({
    calendarsEnabled,
    canCreateOffer,
    hasEntryAndRange: Boolean(requestedEntry && requestedFrom && requestedTo),
    isEntryInactive,
    selected: formMethods.watch('offerBookingTarget'),
    setValue: formMethods.setValue,
    type: selectedType ?? undefined,
  });
  // the offer fields are validated only while the offer section is ready
  if ((offer.status === 'ready') !== isOfferRequired) setIsOfferRequired(offer.status === 'ready');
  const autoName = processCalendarEntryQueryUtil.buildProcessName(
    {
      bookingMode,
      entryName: requestedEntry?.label,
      from: requestedFrom,
      to: requestedTo,
    },
    {
      entryWithoutRange: entry =>
        t('form.calendarEntryQuery.autoName.entryWithoutRange', { entry }),
      entryWithRange: (entry, range) =>
        t('form.calendarEntryQuery.autoName.entryWithRange', { entry, range }),
      rangeWithoutEntry: range =>
        t('form.calendarEntryQuery.autoName.rangeWithoutEntry', { range }),
    },
  );
  const isContactDirty = Boolean(dirtyFields.contact);
  const isCustomerDirty = Boolean(dirtyFields.customer);
  const isProcessOwnerDirty = Boolean(dirtyFields.processOwnerId);
  const selectedContact = formMethods.watch('contact');
  const selectedCustomer = formMethods.watch('customer');

  useEffect(
    () =>
      processesCreateUtil.resetForm(
        formMethods,
        request,
        userSelectOption,
        initialCalendarEntryQuery,
      ),
    [formMethods, initialCalendarEntryQuery, request, userId, userSelectOption],
  );

  // The form starts in full-day mode; the booking mode of the type is applied once it is known.
  const isBookingModeError = useProcessCalendarEntryQueryBookingMode({
    bookingMode,
    formMethods,
    initialBookingMode: BookingModeDto.FullDay,
    isError: isCalendarEntryQueryError,
    isLoading: isLoadingCalendarEntryQuery,
    prefill: request,
  });

  // Nazwa układa się z kontekstu zapytania, dopóki użytkownik jej nie zmieni.
  useEffect(() => {
    if (!isNameEdited) formMethods.setValue('name', autoName);
  }, [autoName, formMethods, isNameEdited, request]);

  useEffect(() => {
    setSelectedMatch(null);
  }, [request]);

  useEffect(
    () =>
      processesCreateUtil.applyDefaultMatch({
        activeMatch,
        isContactDirty,
        isCustomerDirty,
        isProcessOwnerDirty,
        matchOwnerOption,
        selectedMatch,
        setValue: formMethods.setValue,
      }),
    [
      activeMatch,
      formMethods.setValue,
      isContactDirty,
      isCustomerDirty,
      isProcessOwnerDirty,
      matchOwnerOption,
      selectedMatch,
    ],
  );

  useEffect(
    () => processesCreateUtil.synchronizeFormDetails(formMethods, contact, customer),
    [contact, customer, formMethods],
  );

  const handleClose = () => setRequest(null);
  const handleCreated = () => {
    handleClose();
    if (request.emailThreadId) void refetchCurrentThreads(request.emailThreadId);
  };
  const handleSubmit = processesCreateUtil.createSubmitHandler({
    addResourceNote,
    bookingMode,
    canOpenOffer: hasRouteAccess(RouteEnum.PROCESSES_DETAILS_OFFER_INSTANCE, permissions),
    canOpenProcess: hasRouteAccess(RouteEnum.PROCESSES_DETAILS, permissions),
    createProcess,
    includeOffer: offer.status === 'ready',
    onCreated: handleCreated,
    onInvalidContact: () =>
      formMethods.setError('contact', {
        message: t('form.customerRequiredForContact'),
        type: 'validate',
      }),
    t,
    userId,
  });

  const isDisabled = isCreatingProcess || isAddingResourceNote;
  const isSubmitDisabled =
    isDisabled ||
    isGettingDefaultData ||
    isLoadingMatchStatuses ||
    isLoadingCalendarEntryQuery ||
    offer.status === 'loading';
  const handleDialogClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (!isDisabled && reason !== 'backdropClick') handleClose();
  };

  return (
    <Dialog
      aria-labelledby="process-create-dialog-title"
      fullWidth
      maxWidth="md"
      onClose={handleDialogClose}
      open
    >
      <DialogTitle id="process-create-dialog-title">{t('title')}</DialogTitle>
      <FormProviderRc {...formMethods} validation={validation}>
        <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
          <DialogContent dividers>
            <Stack spacing={2}>
              {request.emailThreadId && request.senderEmail && (
                <Stack spacing={1.5}>
                  {isGettingDefaultData && <CircularProgress size={24} />}
                  {isDefaultDataError && (
                    <Typography color="error" role="alert" variant="f14Regular">
                      {emailT('defaultDataError')}
                    </Typography>
                  )}
                  {processCreationDefaultData && matches.length === 0 && (
                    <Typography component="div" variant="f14Regular">
                      {emailT('noContactMatch')}
                    </Typography>
                  )}
                  {matches.length > 0 && (
                    <>
                      <Typography component="div" variant="f14Medium">
                        {emailT('selectMatch')}
                      </Typography>
                      <Box sx={processCreateDialogStyles.matchOptions}>
                        {matches.map(match => {
                          const isSelected =
                            activeMatch?.contactId === match.contactId &&
                            activeMatch?.customerId === match.customerId &&
                            selectedContact?.contactId === match.contactId &&
                            selectedContact?.customerId === match.customerId &&
                            selectedCustomer?.customerId === match.customerId;
                          const matchStatus = matchStatuses[match.customerId];
                          // only an ACTIVE customer can become the customer of a process
                          const isSelectable = processesCreateUtil.isMatchSelectable(
                            match,
                            matchStatuses,
                          );
                          return (
                            <ButtonBase
                              aria-pressed={isSelected}
                              disabled={!isSelectable}
                              key={`${match.customerId}-${match.contactId}`}
                              onClick={() =>
                                processesCreateUtil.selectMatch(
                                  formMethods.setValue,
                                  setSelectedMatch,
                                  USERS_SHORT,
                                  match,
                                )
                              }
                              sx={processCreateDialogStyles.matchOption(isSelected, isSelectable)}
                              type="button"
                            >
                              <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                                <Typography variant="f14Medium">{match.customerName}</Typography>
                                {matchStatus && <CustomerStatusChip status={matchStatus} />}
                                {!isSelectable && (
                                  <Typography color="textSecondary" variant="f14Regular">
                                    {tPicker(
                                      customerStatusUtil.processCustomerBlockedReason(matchStatus),
                                    )}
                                  </Typography>
                                )}
                                <Typography color="textSecondary" variant="f14Regular">
                                  {emailT('matchContact')}: {match.contactName}
                                </Typography>
                                <Typography color="textSecondary" variant="f14Regular">
                                  {emailT('matchEmail')}: {match.email}
                                </Typography>
                                <Typography color="textSecondary" variant="f14Regular">
                                  {emailT('matchOwner')}: {match.ownerFirstName}{' '}
                                  {match.ownerLastName}
                                </Typography>
                              </Stack>
                            </ButtonBase>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </Stack>
              )}
              <ProcessCreateForm
                calendarEntryQueryOptions={{
                  bookingMode,
                  entryOptions,
                  isBookingModeError,
                  typeOptions,
                }}
                disabled={isDisabled}
                offer={offer}
                onNameChange={() => setNameEditedFor(request)}
                usersOptions={usersOptions}
              />
            </Stack>
            {formMethods.formState.errors.contact?.message && (
              <FormHelperText error>{formMethods.formState.errors.contact.message}</FormHelperText>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={isDisabled} onClick={handleClose} variant="outlined">
              {t('button.cancel')}
            </Button>
            <Button
              disabled={isSubmitDisabled || isBookingModeError}
              loading={isSubmitDisabled}
              type="submit"
            >
              {t('button.save')}
            </Button>
          </DialogActions>
        </form>
      </FormProviderRc>
    </Dialog>
  );
};
